import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import "./NewSidebar.scss";
import { Modal } from "react-bootstrap";
import { getSubscriptionPlans } from "../../../Redux/ActionCreators/userAction";
import { Snackbar, Alert } from "@mui/material";

const PAYG_PLAN_ID = "fa2aae05-c4ec-411d-adac-9d4d3a4053df";

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userSectionRef = useRef(null);
  const dispatch = useDispatch();
  const [isUserOpen, setIsUserOpen] = useState(false);
  const [sidebarClosed, setSideBarClosed] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [subscriptionPopup, setSubscriptionPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({open: false, message: "", severity: "error"});

  const { menuList, adminMenuList } = useSelector((state) => state.layout);
  const { selectedTenant } = useSelector((state) => state.users)

  const details = JSON.parse(localStorage.getItem("profileDetails"));
  const currentPlanType = selectedTenant?.data?.subscription_plan?.plan_type;
  const isUsageCurrentPlan = currentPlanType === "usage";
  const roleId = details?.roleId;
  const superAdminId = "1";

  const isPayAsYouGo = selectedTenant?.data?.subscription_plan?.id === PAYG_PLAN_ID;

  const isMobile = window.innerWidth < 767;

  // Filter the menuList based on roleId. If roleId is "1", exclude the first item.
  const baseMenu = roleId === superAdminId ? adminMenuList : menuList;
  const filteredMenuList = baseMenu.filter(item => isMobile ? item.showInMobile !== false : item.showInDesktop !== false);

  useEffect(() => {
    const matchedMenu = filteredMenuList.find((menuItem) => {
      if (!menuItem.subMenu) return false;
      return menuItem.subMenu.some(
        (subItem) => subItem.path === location.pathname
      );
    });

    if (matchedMenu) {
      setOpenDropdown(matchedMenu.name);
    }
  }, [location.pathname, filteredMenuList]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        userSectionRef.current &&
        !userSectionRef.current.contains(event.target)
      ) {
        setIsUserOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleSnackbar = (e) => {
      setSnackbar({ open: true, ...e.detail });
    };
    window.addEventListener("SHOW_SNACKBAR", handleSnackbar);
    return () => window.removeEventListener("SHOW_SNACKBAR", handleSnackbar);
  }, []);

  const toggleUserDropdown = () => {
    setIsUserOpen(!isUserOpen);
    setSideBarClosed(false);
  };

  const toggleSidebar = () => {
    setSideBarClosed(!sidebarClosed);
    setIsUserOpen(false);
    toggleDropdown(false);
  };

  const companyProfile = () => {
    navigate("/profile");
    setIsUserOpen(false);
  };

  const logout = () => {
    localStorage.clear();
    window.location.href = "/login";
  };

  const toggleDropdown = (menuName) => {
    setOpenDropdown(openDropdown === menuName ? null : menuName);
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 767) {
        setSideBarClosed(true);
      } else {
        setSideBarClosed(false);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  
  const handleSubscriptionPlan = async () => {
    const tenantId = localStorage.getItem("tenentId");
    setIsLoading(true);
    try {
      const response = await dispatch(getSubscriptionPlans(tenantId));
      if (response?.status === 200) {
        setSubscriptionPopup(true);
      } else if ([403, 404, 410].includes(response?.parsedStatus || response?.status)) {
        setSnackbar({
          open: true,
          message: "Subscription not found",
          severity: "error"
        });
      }
    } catch (error) {
      if ([403, 404, 410].includes(error?.parsedStatus)) {
        setSnackbar({
          open: true,
          message: "Subscription not found",
          severity: "error"
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`sidebar ${sidebarClosed ? "closed" : ""}`}>
      <div>
      {!isMobile && 
        <div className="logo">
          {sidebarClosed ? (
            <img src="/images/gappeoai-sm-logo.svg" alt="" />
          ) : (
            <img src="/images/gappeoai-logo.png" alt="" />
          )}
        </div>
      }
        {(window.innerWidth > 1000) ? null : (
          (!isMobile &&
            <div className="d-flex justify-content-end">
              <button className="toggle-btn" onClick={toggleSidebar}>
                <img src="/images/collapse-sidebar.svg" alt="" />
              </button>
            </div>
          )
        )}
        <ul className="menu-items">
          {filteredMenuList.map((menuItem, index) => {
            const isActive = location.pathname === menuItem.path;
            const hasSubMenu = menuItem.subMenu;

            return (
              <li
                key={index}
                className={isActive ? `active ${menuItem.name}` : `${menuItem.name}`}
                data-dropdown-open={
                  menuItem.name === "Reports" && openDropdown === "Reports"
                }
                onClick={
                  hasSubMenu
                  ? () => toggleDropdown(menuItem.name)
                  : () => {
                    setOpenDropdown(null);
                    navigate(menuItem.path);
                  }
                }
                >
                <div className="menu-item-content d-flex align-items-center">
                  <img src={`/images/${isMobile && isActive && menuItem.activeIcon ? menuItem.activeIcon : menuItem.icon}`} alt="" />
                  <div
                    className="d-flex"
                    style={{ width: "100%", justifyContent: "space-between" }}
                  >
                    <span>{menuItem.name}</span>
                    {hasSubMenu && (
                      <span
                        className={`dropdown-arrow ${
                          openDropdown === menuItem.name ? "open" : ""
                        }`}
                      >
                        <img src="/images/dropdown-icon.svg" alt="" />
                      </span>
                    )}
                  </div>
                </div>
                {hasSubMenu && openDropdown === menuItem.name && (
                  <ul className="dropdown-menu">
                    {menuItem.subMenu.map((subItem, subIndex) => {
                      const isSubActive = location.pathname === subItem.path;
                      return (
                        <li
                          key={subIndex}
                          onClick={() => navigate(subItem.path)}
                          className={isSubActive ? "active" : ""}
                          style={{marginBottom:'0'}}
                        >
                          {subItem.name}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </div>
      <div
        className="user-section cursor-pointer"
        onClick={toggleUserDropdown}
        ref={userSectionRef}
      >
        {!isMobile ?
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ gap: "10px" }}
          >
            <div className="user-avatar">
              {(() => {
                const name = localStorage.getItem("name") || "";
                const firstChar = name.charAt(0).toUpperCase();
                const secondChar = name.charAt(1)?.toUpperCase();
                return firstChar.trim() ? firstChar : (secondChar || "U");
              })()}
            </div>
            {!sidebarClosed && (
              <div className="user-details">
                <h6>{localStorage.getItem("name")}</h6>
                <p>Recruitment specialist</p>
              </div>
            )}
          </div>
          : 
          <div className="d-flex flex-column align-items-center" style={{gap:'8px'}}>
            <img src="/images/more-bar.svg" alt="" width={20} height={20} />
            {isMobile && (<span>More</span>)}
          </div>
        }
        {(!sidebarClosed && !isMobile) && <div className="three-dots">...</div>}
        {isUserOpen && (
          <ul
            className="dropdown-menu user-dropdown-menu"
            style={{ padding: "10px" }}
          >
            {isMobile && (
            <>
              <li onClick={() => navigate('/gappeo-tests')}>Gappeo Tests</li>
              <li onClick={() => navigate('/my-questions')}>My Questions</li>
              <li onClick={() => navigate('/reports')}>Assessments</li>
              <li onClick={() => navigate('/voice-interviews-report')}>Voice interview</li>
              <li onClick={() => navigate('/video-interviews-report')}>Video interview</li>
            </>
            )}
            <li onClick={() => navigate('/users')}>Users</li>
            <li onClick={handleSubscriptionPlan}>Usage and plan</li>
            <li onClick={companyProfile}>Company Profile</li>
            <li onClick={logout}>Sign Out</li>
          </ul>
        )}
      </div>
      <Modal
        show={subscriptionPopup}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        dialogClassName={"detail-modal"}
      >
        <div className="modal-header" style={{ background: "#f75891" }}>
          <h5 className="modal-title">Subscription Plan</h5>
          <div
            className="sticky-close"
            onClick={() => setSubscriptionPopup(false)}
          >
            <img src="/images/close-white.svg" width="15px" alt="" />
          </div>
        </div>
        <Modal.Body style={{ padding: "22px" }}>
          <div className="row mt-2">
            <div className="col-md-6 col-12">
              <label>Current Plan </label>
              <input
                className="form-control mt-2"
                value={selectedTenant?.data?.subscription_plan?.description}
                disabled
              />
            </div>
            <div className="col-md-6 col-12">
              <label>Expires at</label>
              <input
                className="form-control mt-2"
                value={selectedTenant?.data?.ends_at?.split("T")[0] || ""}
                disabled
              />
            </div>
          </div>
          {isUsageCurrentPlan ? (
                <>
                  {/* TOTAL CREDITS */}
                  <div className="row mt-3">
                    <div className="col-md-6 col-12">
                      <label>Total Credits</label>
                      <input
                        className="form-control mt-2"
                        value={selectedTenant?.data?.total_credits ?? 0}
                        disabled
                      />
                    </div>
                  </div>

                  {/* DEDUCT QUANTITY */}
                  <h4 className="mt-4" style={{ color: "#696873" }}>
                    Credit Usage
                  </h4>

                  <div className="row mt-2">
                    {["VOICE_CALL", "ASSESSMENT", "VIDEO_CALL", "CANDIDATE"].map(
                      (featureCode) => {
                        const usage = selectedTenant?.data?.feature_usages?.find(
                          (u) => u.subscription_feature?.code === featureCode
                        );

                        const labelMap = {
                          VOICE_CALL: "Voice Call",
                          ASSESSMENT: "Assessment",
                          VIDEO_CALL: "Video Call",
                          CANDIDATE: "Candidate",
                        };

                        return (
                          <div className="col-md-6 col-12 mt-3" key={featureCode}>
                            <label>{labelMap[featureCode]}</label>
                            <input
                              className="form-control mt-2"
                              value={usage?.used_credits ?? 0}
                              disabled
                            />
                          </div>
                        );
                      }
                    )}
                  </div>
                </>
              ) : (
                <>
                {["VOICE_CALL", "ASSESSMENT", "VIDEO_CALL", "CANDIDATE"].map((featureCode) => {
                  const usage = selectedTenant?.data?.feature_usages?.find(
                    (u) => u.subscription_feature?.code === featureCode
                  );
                  const labelMap = {
                    VOICE_CALL: "Voice Call Credits",
                    ASSESSMENT: "Assessment Credits",
                    VIDEO_CALL: "Video Call Credits",
                    CANDIDATE: 'Candidate Credits',
                  };

                  return (
                    <div className="row mt-3" key={featureCode}>
                      <h4>{labelMap[featureCode]}</h4>
                      {!isPayAsYouGo && (
                        <>
                          <div className="col-md-4 col-12">
                            <label>Total Credits</label>
                            <input
                              className="form-control mt-2"
                              value={usage?.total_count ?? 0}
                              disabled
                            />
                          </div>
                          <div className="col-md-4 col-12">
                            <label>Remaining Credits</label>
                            <input
                              className="form-control mt-2"
                              value={usage?.remaining_count ?? 0}
                              disabled
                            />
                          </div>
                        </>
                      )}
                      <div className={isPayAsYouGo ? "col-md-6 col-12" : "col-md-4 col-12"}>
                        <label>Used Credits</label>
                        <input
                          className="form-control mt-2"
                          value={usage?.used_count ?? 0}
                          disabled
                        />
                      </div>
                    </div>
                  );
                })}
                </>
              )}
        </Modal.Body>
      </Modal>
      {isLoading && (
        <div className="loading-overlay" style={{position: 'fixed', top:'0', left:'0', width:'100%', height:'100%', backgroundColor:'rgba(0, 0, 0, 0.5)' , color:'#000000', zIndex:'9999', display:'flex', justifyContent:'center', alignItems:'center'}}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Sidebar;
